import type { OnlineSession, OnlineIdentity } from '../online/OnlineIdentity';
import type { ChatSettings } from '../chat/ChatTypes';
import type { MatchConnection, MatchMode, MatchRegion, MatchRegionPreference, MatchmakingSearchRequest, QueueTicket, RankedRecovery,
  RankedRecoveryAuthorization } from '../online/MatchmakingContracts';
import type { OnlineSettings, PublicOnlineSettings } from '../online/OnlineSettings';
import { immutableGameContentSnapshot, type GameContentSnapshot } from '../game/content/GameContentContracts';
import type { AssetLocation } from '../assets/AssetContracts';
import type { LoadingScreenSnapshot } from '../loading/LoadingScreenContracts';

export type AccountRole = 'player' | 'mod' | 'admin';

export interface AccountProfileDto {
  readonly id: string;
  readonly firstName: string | null;
  readonly lastName: string | null;
  readonly displayName: string | null;
  readonly role: AccountRole;
  readonly onboardingComplete: boolean;
  readonly displayNameChangeAvailableAt: string | null;
  readonly createdAt: string;
}

interface ProfileResponse {
  readonly profile: AccountProfileDto;
}

interface ApiErrorResponse {
  readonly error?: {
    readonly code?: string;
    readonly message?: string;
    readonly details?: Readonly<Record<string, string>>;
  };
}

export class AppApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: Readonly<Record<string, string>>,
  ) {
    super(message);
    this.name = 'AppApiError';
  }
}

export class AppApiClient {
  constructor(
    private readonly getAccessToken: () => Promise<string>,
    private readonly baseUrl = (import.meta.env.VITE_APP_SERVER_URL as string | undefined)?.trim() ?? '',
  ) {}

  async getProfile(): Promise<AccountProfileDto> {
    return (await this.request<ProfileResponse>('/api/me')).profile;
  }

  createOnlineSession(authenticated: boolean, guestToken?: string): Promise<OnlineSession> {
    return this.request('/api/online/session', {
      method: 'POST', ...(guestToken && !authenticated ? { body: JSON.stringify({ guestToken }) } : {}),
    }, authenticated);
  }

  async getOnlineIdentity(token: string): Promise<OnlineIdentity> {
    return (await this.request<{ identity: OnlineIdentity }>('/api/online/identity', {
      headers: { Authorization: `Bearer ${token}` },
    }, false)).identity;
  }

  getMatchmaking(token: string): Promise<{ ticket: QueueTicket | null; banUntil: string | null;
    availability: Pick<OnlineSettings, 'casualEnabled' | 'rankedEnabled'>; regions: readonly MatchRegion[] }> {
    return this.request('/api/online/matchmaking', { headers: { Authorization: `Bearer ${token}` } }, false);
  }

  async fetchRankedRecovery(token: string): Promise<RankedRecovery | null> {
    return (await this.request<{ recovery: RankedRecovery | null }>('/api/online/matchmaking/recovery', {
      headers: { Authorization: `Bearer ${token}` },
    }, false)).recovery;
  }

  async acceptRankedRecovery(token: string, matchId: string): Promise<RankedRecoveryAuthorization> {
    return (await this.request<{ authorization: RankedRecoveryAuthorization }>('/api/online/matchmaking/recovery/accept', {
      method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: JSON.stringify({ matchId }),
    }, false)).authorization;
  }

  declineRankedRecovery(token: string, matchId: string): Promise<void> {
    return this.request('/api/online/matchmaking/recovery/decline', {
      method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: JSON.stringify({ matchId }),
    }, false).then(() => undefined);
  }

  enqueueMatchmaking(token: string, search: MatchmakingSearchRequest): Promise<{ ticket: QueueTicket }> {
    return this.request('/api/online/matchmaking', {
      method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: JSON.stringify(search),
    }, false);
  }

  cancelMatchmaking(token: string): Promise<void> {
    return this.request('/api/online/matchmaking', {
      method: 'DELETE', headers: { Authorization: `Bearer ${token}` },
    }, false).then(() => undefined);
  }

  allocatePrivateMatchServer(token: string,
    regionPreference: MatchRegionPreference = { mode: 'automatic' }): Promise<{ allocationId: string; connection: MatchConnection }> {
    return this.request('/api/online/match-servers', {
      method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: JSON.stringify({ regionPreference }),
    }, false);
  }

  bindPrivateMatchRoom(token: string, allocationId: string, roomCode: string): Promise<void> {
    return this.request(`/api/online/match-servers/${encodeURIComponent(allocationId)}/room`, {
      method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: JSON.stringify({ roomCode }),
    }, false).then(() => undefined);
  }

  releasePrivateMatchServer(token: string, allocationId: string): Promise<void> {
    return this.request(`/api/online/match-servers/${encodeURIComponent(allocationId)}`, {
      method: 'DELETE', headers: { Authorization: `Bearer ${token}` },
    }, false).then(() => undefined);
  }

  resolvePrivateMatchRoom(roomCode: string): Promise<{ connection: MatchConnection }> {
    return this.request(`/api/online/room-routes/${encodeURIComponent(roomCode)}`, {}, false);
  }

  getOnlineRating(token: string, mode: MatchMode, format: 1 | 2 | 3): Promise<{ queue: { mode: MatchMode; format: 1 | 2 | 3 };
    rating: { rating: number; games: number } }> {
    return this.request(`/api/online/rating?mode=${mode}&format=${format}`, {
      headers: { Authorization: `Bearer ${token}` },
    }, false);
  }

  getOnlineSettings(): Promise<PublicOnlineSettings> {
    return this.request<{ settings: PublicOnlineSettings }>('/api/online/settings', {}, false).then(({ settings }) => settings);
  }

  async getGameContentSnapshot(): Promise<GameContentSnapshot> {
    const { snapshot } = await this.request<{ snapshot: GameContentSnapshot }>('/api/game-content/snapshot', {}, false);
    return immutableGameContentSnapshot(snapshot);
  }

  getLoadingScreens(signal?: AbortSignal): Promise<LoadingScreenSnapshot> {
    return this.request<LoadingScreenSnapshot>('/api/loading-screens', { signal }, false);
  }

  resolveLoadingScreenRendition(renditionId: string, signal: AbortSignal): Promise<AssetLocation> {
    return this.request<AssetLocation>(`/api/loading-screens/renditions/${encodeURIComponent(renditionId)}/resolve`, {
      method: 'POST', signal,
    }, false);
  }

  getAdminOnlineSettings(): Promise<OnlineSettings> {
    return this.request<{ settings: OnlineSettings }>('/api/admin/online/settings').then(({ settings }) => settings);
  }

  updateAdminOnlineSettings(revision: number, patch: Partial<Omit<OnlineSettings, 'revision'>>): Promise<OnlineSettings> {
    return this.request<{ settings: OnlineSettings }>('/api/admin/online/settings', {
      method: 'PATCH', body: JSON.stringify({ revision, ...patch }),
    }).then(({ settings }) => settings);
  }

  async getChatSettings(): Promise<ChatSettings> {
    return (await this.request<{ settings: ChatSettings }>('/api/chat/settings', {}, false)).settings;
  }

  async getAdminChatSettings(): Promise<ChatSettings> {
    return (await this.request<{ settings: ChatSettings }>('/api/admin/chat/settings')).settings;
  }

  async updateChatSettings(patch: Partial<ChatSettings>): Promise<ChatSettings> {
    return (await this.request<{ settings: ChatSettings }>('/api/admin/chat/settings', {
      method: 'PATCH', body: JSON.stringify(patch),
    })).settings;
  }

  get globalChatUrl(): string {
    const url = new URL(`${this.baseUrl}/api/chat/global`, window.location.origin);
    url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
    return url.toString();
  }

  async completeOnboarding(input: {
    readonly firstName: string;
    readonly lastName: string;
    readonly displayName: string;
  }): Promise<AccountProfileDto> {
    return (await this.request<ProfileResponse>('/api/onboarding', {
      method: 'POST',
      body: JSON.stringify(input),
    })).profile;
  }

  async changeDisplayName(displayName: string): Promise<AccountProfileDto> {
    return (await this.request<ProfileResponse>('/api/me/display-name', {
      method: 'PATCH',
      body: JSON.stringify({ displayName }),
    })).profile;
  }

  async request<T>(path: string, init: RequestInit = {}, authenticated = true): Promise<T> {
    const token = authenticated ? await this.getAccessToken() : null;
    const headers = new Headers(init.headers);
    if (token) headers.set('Authorization', `Bearer ${token}`);
    if (init.body && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json');
    const response = await fetch(`${this.baseUrl}${path}`, {
      ...init,
      headers,
    });
    const body = await readJson(response);
    if (!response.ok) {
      if (body === null) {
        throw new AppApiError(
          response.status,
          'app-server-unavailable',
          'Application server is unavailable or returned an invalid upstream response.',
        );
      }
      const failure = body as ApiErrorResponse;
      throw new AppApiError(
        response.status,
        failure.error?.code ?? 'request-failed',
        failure.error?.message ?? `Request failed with status ${response.status}.`,
        failure.error?.details,
      );
    }
    return body as T;
  }
}

async function readJson(response: Response): Promise<unknown> {
  if (response.status === 204) return undefined;
  const contentType = response.headers.get('content-type')?.toLowerCase() ?? '';
  if (!contentType.includes('application/json')) {
    if (response.ok) throw new Error('Application server returned a non-JSON success response.');
    return null;
  }
  try {
    return await response.json();
  } catch {
    if (response.ok) throw new Error('Application server returned an invalid JSON response.');
    return null;
  }
}
